import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search } from 'lucide-react';
import { FramedAvatar } from '@/components/ui/FramedAvatar';
import { useConversations } from '@/shared/hooks/useConversations';
import { useMessageableCandidates } from '@/shared/hooks/useMessageableCandidates';
import { getInitials } from '@/utils/avatarUtils';

/**
 * Contact picker — lets the user choose someone to start a conversation with.
 * The candidate scope (connections vs all users) is determined by the caller's
 * role via useMessageableCandidates — this component knows nothing about admin.
 * Route: /messages/new
 */
const NewConversation: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  const { candidates, isLoading, scopeLabel } = useMessageableCandidates();
  const { createOrGetConversation, isCreating, refetchConversations } = useConversations();

  const filtered = useMemo(() => {
    if (!query.trim()) return candidates;
    const q = query.toLowerCase();
    return candidates.filter(
      (c) =>
        c.full_name?.toLowerCase().includes(q) ||
        c.username?.toLowerCase().includes(q),
    );
  }, [candidates, query]);

  const handleSelect = async (userId: string) => {
    try {
      const convId = await createOrGetConversation(userId);
      await refetchConversations();
      navigate(`/messages/${convId}`, { replace: true });
    } catch (err: any) {
      console.error('Could not start conversation:', err);
    }
  };

  return (
    <div
      className="h-screen flex flex-col"
      style={{ background: '#160C0C' }}
    >
      {/* ── Custom header ── */}
      <div
        className="flex items-center gap-3 px-4 flex-shrink-0"
        style={{
          paddingTop: 'calc(env(safe-area-inset-top) + 12px)',
          paddingBottom: '12px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <button
          onClick={() => navigate('/messages')}
          className="p-1 text-white/60 hover:text-white transition-colors"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-white font-semibold text-base">New Message</h1>
      </div>

      {/* ── Search ── */}
      <div className="px-5 pt-5 pb-3 flex-shrink-0">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search..."
            className="w-full text-white placeholder-white/40 text-sm rounded-full pl-9 pr-4 py-2.5 outline-none focus:ring-1 focus:ring-white/20"
            style={{ background: 'rgba(255,255,255,0.08)' }}
          />
        </div>
      </div>

      {/* ── Section label ── */}
      {!query && (
        <p className="px-5 pb-2 text-white/40 text-xs font-semibold uppercase tracking-wider flex-shrink-0">
          {scopeLabel}
        </p>
      )}

      {/* ── List ── */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-white/40 text-sm text-center py-12">
            {query ? 'No users match your search.' : 'No users found.'}
          </p>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map((u) => (
              <button
                key={u.id}
                onClick={() => handleSelect(u.id)}
                disabled={isCreating}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors text-left disabled:opacity-50"
              >
                <FramedAvatar
                  src={u.profile_picture_url ?? undefined}
                  initials={getInitials(u.full_name, '?')}
                  beltRank={u.belt_rank}
                  frameSize={{ w: 40, h: 44 }}
                  isManual
                />

                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold text-sm truncate">
                    {u.full_name ?? u.username}
                  </p>
                  <p className="text-white/50 text-xs truncate">
                    {[
                      u.belt_rank
                        ? u.belt_rank.charAt(0).toUpperCase() +
                          u.belt_rank.slice(1) + ' Belt'
                        : null,
                      u.academy_name,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewConversation;
